import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Alert, Box, Button, Link, Skeleton, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { appBenefits, categoryLabel, client, type Application, type Department, type Feature } from '../lib/api';
import { AppStatusChip } from '../components/StatusChip';
import { AppMonogram, PageHeader, Section } from '../components/Page';
import { tokens } from '../theme';
import { ScreenshotGallery } from '../components/AppScreens';
import { PersonAvatar } from '../components/TeamArt';

export function AppDetailPage() {
  const { slug = '' } = useParams();
  const [application, setApplication] = useState<Application | null>(null);
  const [department, setDepartment] = useState<Department | null>(null);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError('');
    client
      .appDetail(slug)
      .then((data) => {
        setApplication(data.application);
        setDepartment(data.department);
        setFeatures(data.features);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <Section>
        <Stack spacing={2}>
          <Skeleton height={56} width={320} />
          <Skeleton variant="rounded" height={260} />
        </Stack>
      </Section>
    );
  }

  if (error || !application) {
    return (
      <Section>
        <Alert severity="error">{error || 'Application not found'}</Alert>
        <Link component={RouterLink} to="/apps" sx={{ display: 'inline-block', mt: 3 }}>
          ← Back to applications
        </Link>
      </Section>
    );
  }

  const meta = [
    ['Category', categoryLabel(application.category)],
    ['Department', department?.name ?? '—'],
    ['For', application.audience || '—'],
    ['Version', application.version || '—'],
    ...(application.developer ? [['Developed by', application.developer]] : []),
  ];
  const benefits = appBenefits(application);
  const hasImpact = Boolean(application.time_saved || application.cost_saved) || benefits.length > 0;
  const figures = [
    ['Time saved', application.time_saved],
    ['Est. cost saving', application.cost_saved],
  ].filter(([, v]) => v) as Array<[string, string]>;

  return (
    <>
      <PageHeader
        title={application.name}
        subtitle={application.tagline}
        crumbs={[{ label: 'Applications', to: '/apps' }]}
        aside={
          application.url ? (
            <Button
              href={application.url}
              target="_blank"
              rel="noreferrer"
              variant="contained"
              size="large"
              endIcon={<OpenInNewIcon />}
              sx={{ flex: 'none', alignSelf: { xs: 'flex-start', md: 'flex-end' } }}
            >
              Open app
            </Button>
          ) : undefined
        }
      />

      <Section>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={{ xs: 5, md: 8 }}>
          <Stack spacing={4} sx={{ flex: 1.4, minWidth: 0 }}>
            <Stack direction="row" gap={1.5} alignItems="center">
              <AppStatusChip status={application.status} />
            </Stack>
            <Typography fontSize={18} color="#344054" maxWidth={720}>
              {application.description}
            </Typography>
            {hasImpact && (
              <Box>
                <Typography variant="h2" fontSize={24} mb={2}>
                  Impact for the AST team
                </Typography>
                <Box sx={{ border: `1px solid ${tokens.border}`, borderLeft: `4px solid ${tokens.blue}`, borderRadius: 2, p: { xs: 2.5, md: 3 } }}>
                  {figures.length > 0 && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 5 }} mb={benefits.length ? 2.5 : 0}>
                      {figures.map(([label, value]) => (
                        <Box key={label}>
                          <Typography fontSize={14} color="text.secondary">
                            {label}
                          </Typography>
                          <Typography fontSize={{ xs: 26, md: 30 }} fontWeight={800} color="primary.main" lineHeight={1.2}>
                            {value}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                  {benefits.length > 0 && (
                    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 1.25 }}>
                      {benefits.map((b, i) => (
                        <Box component="li" key={i} sx={{ display: 'grid', gridTemplateColumns: '24px 1fr', gap: 1 }}>
                          <TrendingUpIcon sx={{ color: 'primary.main', fontSize: 20, mt: 0.25 }} aria-hidden />
                          <Typography color={tokens.ink}>{b}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {application.impact_note && (
                    <Typography fontSize={13} color="text.secondary" sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${tokens.divider}`, whiteSpace: 'pre-line' }}>
                      {application.impact_note}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            <ScreenshotGallery app={application} />
            <Box>
              <Typography variant="h2" fontSize={24} mb={2}>
                Features
              </Typography>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {features.map((f) => (
                  <Box
                    component="li"
                    key={f.id}
                    sx={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: 1.5, py: 2, borderTop: `1px solid ${tokens.divider}` }}
                  >
                    <CheckIcon sx={{ color: 'primary.main', mt: 0.25 }} aria-hidden />
                    <Box>
                      <Typography fontWeight={600} color={tokens.ink}>
                        {f.title}
                      </Typography>
                      <Typography color="text.secondary">{f.detail}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Stack>

          <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
            <AppMonogram name={application.name} slug={application.slug} logoUrl={application.logo_url} size="hero" />
            <Box component="dl" sx={{ m: 0, display: 'grid', gap: 2 }}>
              {meta.map(([k, v]) => (
                <Box key={k} sx={{ pb: 2, borderBottom: `1px solid ${tokens.divider}` }}>
                  <Typography component="dt" fontSize={14} color="text.secondary">
                    {k}
                  </Typography>
                  {k === 'Developed by' ? (
                    <Stack component="dd" direction="row" alignItems="center" gap={1.25} m={0} mt={0.75}>
                      <PersonAvatar name={v} />
                      <Typography fontWeight={600} color={tokens.ink}>
                        {v}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography component="dd" fontWeight={600} color={tokens.ink} m={0}>
                      {v}
                    </Typography>
                  )}
                </Box>
              ))}
              <Box>
                <Typography component="dt" fontSize={14} color="text.secondary" mb={1}>
                  Stack
                </Typography>
                <Stack component="dd" direction="row" gap={0.75} flexWrap="wrap" m={0}>
                  {application.stack
                    .split('·')
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .map((s) => (
                      <Box key={s} component="span" sx={{ px: 1.25, py: 0.5, borderRadius: 1, bgcolor: tokens.strip, fontSize: 13, color: '#344054' }}>
                        {s}
                      </Box>
                    ))}
                </Stack>
              </Box>
            </Box>
            <Link component={RouterLink} to="/status">
              Check service status →
            </Link>
          </Stack>
        </Stack>
      </Section>
    </>
  );
}
